/**
 * @fileoverview Paddle Connect Service - API client for Builder Service
 * @module paddle-connect/services/PaddleConnectService
 */

import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type {
  PaddleConnectionStatus,
  PaddleProduct,
  PaddlePrice,
  ConnectPaddleRequest,
  ConnectPaddleResponse,
} from '../types';

/**
 * Get Builder Service URL based on environment
 */
function getBuilderServiceUrl(): string {
  return import.meta.env.VITE_BUILDER_API_URL || 'http://localhost:8081';
}

/**
 * Paddle Connect Service
 * Handles API calls to Builder Service for Paddle operations
 *
 * Uses Firebase Auth directly for authentication (same pattern as StripeConnectService)
 */
export class PaddleConnectService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getBuilderServiceUrl();
  }

  /**
   * Get authentication headers from Firebase Auth
   * Same pattern as StripeConnectService.getAuthHeaders()
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const auth = CoreFirebaseService.getInstance().auth;
    const token = await auth.currentUser?.getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }

    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * Make authenticated API request
   */
  private async request<T>(
    method: 'GET' | 'POST' | 'DELETE',
    path: string,
    body?: object
  ): Promise<T> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${this.baseUrl}/api${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || error.details || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async updateWebhookSecret(projectId: string, environment: 'sandbox' | 'production', webhookSecret: string): Promise<void> {
    await this.request('POST', `/paddle/webhook-secret?projectId=${encodeURIComponent(projectId)}`, { environment, webhookSecret });
  }

  /**
   * Connect Paddle account to project
   *
   * @param projectId - Project ID
   * @param credentials - Paddle API credentials
   */
  async connect(
    projectId: string,
    credentials: ConnectPaddleRequest
  ): Promise<ConnectPaddleResponse> {
    return this.request<ConnectPaddleResponse>(
      'POST',
      `/paddle/connect?projectId=${encodeURIComponent(projectId)}`,
      credentials
    );
  }

  /**
   * Get Paddle connection status
   *
   * @param projectId - Project ID
   */
  async getStatus(projectId: string): Promise<PaddleConnectionStatus> {
    return this.request<PaddleConnectionStatus>(
      'GET',
      `/paddle/status?projectId=${encodeURIComponent(projectId)}`
    );
  }

  /**
   * Disconnect Paddle from project
   *
   * @param projectId - Project ID
   * @param environment - Optional specific environment to disconnect ('sandbox' | 'production' | 'all')
   */
  async disconnect(
    projectId: string,
    environment: 'sandbox' | 'production' | 'all' = 'all'
  ): Promise<{ success: boolean; disconnectedEnvironment: string }> {
    return this.request<{ success: boolean; disconnectedEnvironment: string }>(
      'POST',
      '/paddle/disconnect',
      { projectId, environment }
    );
  }

  /**
   * List products from Paddle account
   *
   * @param projectId - Project ID
   * @param environment - Paddle environment to list products from
   */
  async listProducts(
    projectId: string,
    environment: 'sandbox' | 'production'
  ): Promise<PaddleProduct[]> {
    const response = await this.request<{ products: PaddleProduct[] }>(
      'GET',
      `/paddle/products?projectId=${encodeURIComponent(projectId)}&environment=${environment}`
    );
    return response.products;
  }

  /**
   * List prices from Paddle account
   *
   * @param projectId - Project ID
   * @param environment - Paddle environment to list prices from
   * @param productId - Optional product ID to filter by
   */
  async listPrices(
    projectId: string,
    environment: 'sandbox' | 'production',
    productId?: string
  ): Promise<PaddlePrice[]> {
    let path = `/paddle/prices?projectId=${encodeURIComponent(projectId)}&environment=${environment}`;
    if (productId) {
      path += `&productId=${encodeURIComponent(productId)}`;
    }
    const response = await this.request<{ prices: PaddlePrice[] }>('GET', path);
    return response.prices;
  }

  /**
   * Switch Paddle environment
   *
   * If credentials are provided, connects the new environment first.
   * Otherwise, just switches the active environment to an existing one.
   *
   * @param projectId - Project ID
   * @param credentials - Paddle credentials (optional if env already connected)
   */
  async switchEnvironment(
    projectId: string,
    credentials: Partial<ConnectPaddleRequest> & { environment: 'sandbox' | 'production' }
  ): Promise<ConnectPaddleResponse> {
    return this.request<ConnectPaddleResponse>('POST', '/paddle/switch-environment', {
      projectId,
      ...credentials,
    });
  }

  /**
   * Set active Paddle environment (without providing new credentials)
   *
   * @param projectId - Project ID
   * @param environment - Environment to set as active
   */
  async setActiveEnvironment(
    projectId: string,
    environment: 'sandbox' | 'production'
  ): Promise<{ success: boolean; activeEnvironment: string }> {
    return this.request<{ success: boolean; activeEnvironment: string }>(
      'POST',
      '/paddle/set-active',
      { projectId, environment }
    );
  }
}

/**
 * Create Paddle Connect Service instance
 * No longer requires getIdToken - uses Firebase Auth directly
 */
export function createPaddleConnectService(): PaddleConnectService {
  return new PaddleConnectService();
}

export default PaddleConnectService;
